'use client'

import { useState, useEffect } from 'react'
import { useRouter, useParams } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { formatDT, formatTime } from '@/lib/utils'
import { ArrowLeft, Phone, Plus, Trash2, X, TrendingUp, TrendingDown, ShoppingBag, Edit2 } from 'lucide-react'
import Link from 'next/link'

type Modal = 'avance' | 'paiement' | 'edit' | null

export default function ClientDetailPage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()
  const { id } = useParams<{ id: string }>()

  const [client, setClient] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<Modal>(null)
  const [montant, setMontant] = useState('')
  const [note, setNote] = useState('')
  const [editForm, setEditForm] = useState({ name: '', phone: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    load()
  }, [session])

  async function load() {
    setLoading(true)
    const data = await api.getClient(id)
    setClient(data)
    setEditForm({ name: data.name, phone: data.phone || '' })
    setLoading(false)
  }

  async function submitTransaction(type: 'avance' | 'paiement') {
    if (!montant) return
    setSaving(true)
    await fetch(`/api/clients/${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, montant: parseFloat(montant), note: note || null }),
    })
    setMontant(''); setNote(''); setModal(null)
    setSaving(false)
    load()
  }

  async function saveEdit() {
    if (!editForm.name.trim()) return
    setSaving(true)
    await api.updateClient(id, editForm)
    setModal(null); setSaving(false); load()
  }

  async function deleteClient() {
    if (!confirm('Supprimer ce client ?')) return
    await api.deleteClient(id)
    router.replace('/clients')
  }

  if (loading || !client) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  const solde = parseFloat(client.solde) || 0
  const hasDette = solde < 0
  const hasCredit = solde > 0

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/clients" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1 truncate">{client.name}</h1>
          <button onClick={() => setModal('edit')} className="p-2 text-gray-400">
            <Edit2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-3">
        {/* Solde card */}
        <div className={`rounded-3xl p-6 text-white ${
          hasDette ? 'bg-gradient-to-br from-red-500 to-red-600'
          : hasCredit ? 'bg-gradient-to-br from-green-500 to-green-600'
          : 'bg-gradient-to-br from-gray-500 to-gray-600'
        }`}>
          <p className="text-sm opacity-70 mb-1">
            {hasDette ? '⚠️ Dette restante' : hasCredit ? '✅ Crédit disponible' : '✓ Soldé'}
          </p>
          <p className="text-5xl font-bold mb-2">
            {hasDette ? '' : hasCredit ? '+' : ''}{formatDT(Math.abs(solde))}
          </p>
          {hasDette && <p className="text-sm opacity-80">Le client doit payer {formatDT(Math.abs(solde))}</p>}
          {hasCredit && <p className="text-sm opacity-80">Crédit disponible pour achats</p>}
          {client.phone && (
            <p className="mt-3 text-sm opacity-70 flex items-center gap-1.5">
              <Phone className="w-4 h-4" /> {client.phone}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          {/* Recevoir paiement — quand il y a une dette */}
          <button onClick={() => { setMontant(''); setNote(''); setModal('paiement') }}
            className={`py-4 rounded-2xl font-bold text-white flex flex-col items-center gap-1.5 active:scale-95 ${hasDette ? 'bg-green-500' : 'bg-green-200'}`}>
            <TrendingDown className="w-5 h-5" />
            <span className="text-sm">Paiement reçu</span>
            {hasDette && <span className="text-xs opacity-75">Rembourse sa dette</span>}
          </button>

          {/* Avance — client donne cash à l'avance */}
          <button onClick={() => { setMontant(''); setNote(''); setModal('avance') }}
            className="py-4 bg-indigo-600 rounded-2xl font-bold text-white flex flex-col items-center gap-1.5 active:scale-95">
            <TrendingUp className="w-5 h-5" />
            <span className="text-sm">Avance</span>
            <span className="text-xs opacity-75">Pré-paiement</span>
          </button>
        </div>

        {/* Résumé kardé */}
        {hasDette && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="font-bold text-red-700 mb-2">📋 Situation kardé</p>
            <div className="space-y-1.5 text-sm">
              {(() => {
                const txs = client.transactions ?? []
                const sum = (t: string) => txs.filter((x: any) => x.type === t)
                  .reduce((acc: number, x: any) => acc + (parseFloat(x.montant) || 0), 0)
                const totalCredit   = sum('vente')     // dette nette portée au compte
                const totalPaiements = sum('paiement')
                const totalAvances   = sum('avance')
                return (
                  <>
                    <div className="flex justify-between text-red-600">
                      <span>Achats à crédit (net)</span>
                      <span className="font-semibold">{formatDT(totalCredit)}</span>
                    </div>
                    {totalAvances > 0 && (
                      <div className="flex justify-between text-indigo-600">
                        <span>Avances données</span>
                        <span className="font-semibold">-{formatDT(totalAvances)}</span>
                      </div>
                    )}
                    {totalPaiements > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Paiements reçus</span>
                        <span className="font-semibold">-{formatDT(totalPaiements)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-red-700 border-t border-red-200 pt-1.5 mt-0.5 text-base">
                      <span>Reste à payer</span>
                      <span>{formatDT(Math.abs(solde))}</span>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        )}

        {/* Historique */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <p className="px-4 py-3 font-bold text-gray-800 border-b border-gray-100">Historique</p>
          {!client.transactions?.length ? (
            <p className="text-center text-gray-400 py-8 text-sm">Aucune transaction</p>
          ) : client.transactions.map((tx: any) => {
            const m = parseFloat(tx.montant) || 0
            const isPlus = tx.type === 'avance' || tx.type === 'paiement'
            const icons: Record<string, any> = {
              avance:   { icon: TrendingUp,   color: 'bg-indigo-100 text-indigo-600', label: 'Avance' },
              paiement: { icon: TrendingDown, color: 'bg-green-100 text-green-600',   label: 'Paiement reçu' },
              vente:    { icon: ShoppingBag,  color: 'bg-orange-100 text-orange-600', label: 'Achat à crédit' },
            }
            const cfg = icons[tx.type] || icons.vente
            const Icon = cfg.icon
            return (
              <div key={tx.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 text-sm">{cfg.label}</p>
                  {tx.note && <p className="text-xs text-gray-400 truncate">{tx.note}</p>}
                  <p className="text-xs text-gray-400">{formatTime(tx.created_at)}</p>
                </div>
                <p className={`font-bold text-sm ${isPlus ? 'text-green-600' : 'text-orange-600'}`}>
                  {isPlus ? '+' : '-'}{formatDT(m)}
                </p>
              </div>
            )
          })}
        </div>

        <button onClick={deleteClient}
          className="w-full py-3 text-red-400 font-semibold text-sm flex items-center justify-center gap-2 active:scale-95">
          <Trash2 className="w-4 h-4" /> Supprimer ce client
        </button>
      </div>

      {/* ── Modal paiement / avance ── */}
      {(modal === 'paiement' || modal === 'avance') && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModal(null)} />
          <div className="relative w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {modal === 'paiement' ? '💵 Paiement reçu' : '💰 Avance client'}
                </h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  {modal === 'paiement' ? 'Le client rembourse sa dette' : 'Le client pré-paie'}
                </p>
              </div>
              <button onClick={() => setModal(null)} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div className="relative">
                <input
                  type="number" inputMode="decimal" autoFocus
                  value={montant} onChange={e => setMontant(e.target.value)}
                  placeholder="Montant..."
                  className="w-full px-4 py-4 pr-12 text-2xl font-bold rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-green-500"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">DT</span>
              </div>
              <input
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Note (optionnel)"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-green-500"
              />
            </div>

            {/* Preview nouveau solde */}
            {montant && (
              <div className="bg-gray-50 rounded-2xl p-3 mb-4 space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Solde actuel</span>
                  <span className={solde < 0 ? 'text-red-500 font-semibold' : 'text-green-600 font-semibold'}>
                    {formatDT(solde)}
                  </span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>+ Paiement</span>
                  <span className="font-semibold">+{formatDT(parseFloat(montant) || 0)}</span>
                </div>
                <div className="flex justify-between font-bold border-t border-gray-200 pt-1.5">
                  <span>Nouveau solde</span>
                  <span className={(solde + (parseFloat(montant) || 0)) < 0 ? 'text-red-500' : 'text-green-600'}>
                    {formatDT(solde + (parseFloat(montant) || 0))}
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={() => submitTransaction(modal as 'avance' | 'paiement')}
              disabled={saving || !montant}
              className="w-full py-4 bg-green-500 text-white rounded-2xl font-bold text-lg disabled:opacity-50 active:scale-95"
            >
              {saving ? '...' : `Confirmer ${montant ? formatDT(parseFloat(montant)) : ''} ✓`}
            </button>
          </div>
        </div>
      )}

      {/* ── Modal edit ── */}
      {modal === 'edit' && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModal(null)} />
          <div className="relative w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-800">Modifier client</h2>
              <button onClick={() => setModal(null)} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="space-y-3 mb-5">
              <input autoFocus value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Nom *"
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500" />
              <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Téléphone" type="tel"
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500" />
            </div>
            <button onClick={saveEdit} disabled={saving || !editForm.name.trim()}
              className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 active:scale-95">
              {saving ? '...' : 'Enregistrer ✓'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
